"use client"

import React from "react"
import { Minus, Plus } from "lucide-react"

import { cn } from "@/lib/utils"
import { Badge } from "@/shadcn/components/ui/badge"
import { Button } from "@/shadcn/components/ui/button"
import { ButtonGroup } from "@/shadcn/components/ui/button-group"

// Stepper Card. Figma component set: S311lGXE6JSyTnNAXHmtR7 node 27883:739
// (States: Default / Changed / Saving / Success / TierMax / Error / Disabled).
// A label + current total + a +/- Number Stepper. Resting states carry a helper
// line; editing states add a delta pill beside the total, a price change over
// the new total, and Cancel + a primary action. The actions row wraps, so on a
// narrow card the buttons drop below the price (Figma 30237-5606).

export type StepperState =
  | "default"
  | "changed"
  | "saving"
  | "success"
  | "tierMax"
  | "error"
  | "disabled"

export type StepperCardData = {
  label: string
  value: string | number
  /** Rendered in place of `value`; the stepper keeps counting in `value` units. */
  displayValue?: React.ReactNode
  state?: StepperState
  /** Pill beside the total in the editing states, e.g. "+5 outputs". */
  delta?: string
  /** Price change shown in the editing states, e.g. "+$10/mo". */
  priceDelta?: string
  /** New plan total shown under the price change, e.g. "$144.95/mo". */
  newTotal?: string
  newTotalLabel?: string
  /** Helper line in the resting states (default / success / tierMax / disabled). */
  description?: string
  /** Primary action label in the editing states, e.g. "Add 1 pack". */
  confirmLabel?: string
  /** Disables the primary action, e.g. while a usage error stands. */
  confirmDisabled?: boolean
  /** Message shown in the error state. */
  errorMessage?: React.ReactNode
  /** Helper line shown in the tierMax state, with an inline upgrade link. */
  tierMaxNote?: { before: string; cta: string; after: string }
  className?: string
  onClick: (qty: number) => void
  onConfirm: () => void
  onCancel: () => void
}

const DEFAULT_ERROR = "Couldn't update. Check payment method."
const DEFAULT_TIER_NOTE = {
  before: "Pro tier max.",
  cta: "Upgrade to Plus",
  after: "for up to 250.",
}

export function StepperCard({
  label,
  value,
  displayValue,
  state = "default",
  delta,
  priceDelta,
  newTotal,
  newTotalLabel = "New total",
  description,
  confirmLabel = "Save changes",
  confirmDisabled = false,
  errorMessage = DEFAULT_ERROR,
  tierMaxNote = DEFAULT_TIER_NOTE,
  className,
  onClick,
  onConfirm,
  onCancel,
}: StepperCardData) {

  const editing = state === "changed" || state === "saving" || state === "error"
  const count = typeof value === "string" ? parseInt(value, 10) : value
  const locked = state === "disabled" || state === "saving"

  return (
    <div
      className={cn(
        "tw:flex tw:flex-col tw:gap-2 tw:rounded-lg tw:bg-card tw:px-4 tw:py-3",
        state === "changed" || state === "saving"
          ? "tw:border-2 tw:border-primary"
          : state === "error"
            ? "tw:border-2 tw:border-destructive/40"
            : "tw:border tw:border-border",
        state === "disabled" && "tw:opacity-50",
        className
      )}
    >
      <span className="tw:text-sm tw:text-muted-foreground">{label}</span>

      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <div className="tw:flex tw:min-w-0 tw:flex-wrap tw:items-center tw:gap-2">
          <span className="tw:text-sm tw:font-medium">{displayValue ?? value}</span>
          {editing && delta ? (
            <Badge variant="secondary" className="tw:bg-muted tw:font-normal">
              {delta}
            </Badge>
          ) : null}
        </div>
        <ButtonGroup>
          <Button
            variant="outline"
            size="icon-sm"
            disabled={locked || count <= 0}
            aria-label={`Decrease ${label}`}
            onClick={() => onClick(count - 1)}
          >
            <Minus />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            disabled={locked || state === "tierMax"}
            aria-label={`Increase ${label}`}
            onClick={() => onClick(count + 1)}
          >
            <Plus />
          </Button>
        </ButtonGroup>
      </div>

      {!editing && description ? (
        <p className="tw:text-xs tw:text-muted-foreground">{description}</p>
      ) : null}

      {state === "tierMax" ? (
        <p className="tw:text-xs tw:text-muted-foreground">
          {tierMaxNote.before}{" "}
          <button
            type="button"
            className="tw:text-primary tw:hover:underline"
          >
            {tierMaxNote.cta}
          </button>{" "}
          {tierMaxNote.after}
        </p>
      ) : null}

      {state === "error" ? (
        <p className="tw:text-xs tw:text-destructive">{errorMessage}</p>
      ) : null}

      {editing ? (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-x-1.5 tw:gap-y-3 tw:pt-1.5">
          {/* min-w forces the wrap on a phone-width card instead of squeezing
              the new total onto a third line beside the buttons. */}
          <div className="tw:flex tw:min-w-36 tw:flex-1 tw:flex-col tw:gap-0.5 tw:text-xs">
            {priceDelta ? <span className="tw:font-medium">{priceDelta}</span> : null}
            {newTotal ? (
              <span>
                {newTotalLabel}: {newTotal}
              </span>
            ) : null}
          </div>
          <div className="tw:flex tw:items-center tw:gap-1.5">
            <Button
              variant="outline"
              size="sm"
              disabled={state === "saving"}
              className="tw:text-xs"
              onClick={onCancel}
            >
              Cancel
            </Button>
            {state === "saving" ? (
              <Button size="sm" loading className="tw:text-xs">
                Saving...
              </Button>
            ) : (
              <Button
                size="sm"
                className="tw:text-xs"
                disabled={confirmDisabled}
                onClick={onConfirm}
              >
                {confirmLabel}
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
